<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { BaseTable } from 'dashboard/components-next/table';
import templateButtonsImage from 'dashboard/assets/images/conversation-flows/templates/buttons.webp';
import templateCarouselImage from 'dashboard/assets/images/conversation-flows/templates/carosel.webp';
import templateListImage from 'dashboard/assets/images/conversation-flows/templates/list.webp';
import templateWelcomeImage from 'dashboard/assets/images/conversation-flows/templates/welcom.webp';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ConversationFlowRow from './ConversationFlowRow.vue';
import { flowTemplateIds } from './flowTemplates';

const store = useStore();
const router = useRouter();
const { t, locale } = useI18n();

const flows = useMapGetter('conversationFlows/getConversationFlows');
const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('conversationFlows/getUIFlags');

const loading = ref({});
const searchQuery = ref('');
const selectedFlow = ref({});
const selectedTemplate = ref({});
const deleteDialogRef = ref(null);
const previewDialogRef = ref(null);
const creatingTemplateId = ref('');
const importInputRef = ref(null);

const tableHeaders = computed(() => {
  const currentLocale = locale.value;
  if (!currentLocale) return [];
  return [
    '#',
    t('CONVERSATION_FLOWS.TABLE_HEADER.FLOW_NAME'),
    t('CONVERSATION_FLOWS.TABLE_HEADER.STATUS'),
    t('CONVERSATION_FLOWS.TABLE_HEADER.INBOX'),
    t('CONVERSATION_FLOWS.TABLE_HEADER.UPDATED_AT'),
    t('CONVERSATION_FLOWS.TABLE_HEADER.ACTIONS'),
  ];
});

const filteredFlows = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return flows.value;
  return picoSearch(flows.value, query, ['name', 'description', 'status']);
});

const previewCatalog = {
  welcome_buttons: {
    heroImage: templateWelcomeImage,
  },
  chatbot_buttons: {
    heroImage: templateButtonsImage,
  },
  chatbot_list: {
    heroImage: templateListImage,
  },
  cards_carousel: {
    heroImage: templateCarouselImage,
  },
};

const flowTemplates = computed(() => {
  const currentLocale = locale.value;
  if (!currentLocale) return [];
  const templates = {
    welcome_buttons: {
      id: 'welcome_buttons',
      icon: 'i-lucide-handshake',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.WELCOME_BUTTONS.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.WELCOME_BUTTONS.DESCRIPTION'
      ),
      preview: previewCatalog.welcome_buttons,
    },
    chatbot_buttons: {
      id: 'chatbot_buttons',
      icon: 'i-lucide-message-square-more',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_BUTTONS.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_BUTTONS.DESCRIPTION'
      ),
      preview: previewCatalog.chatbot_buttons,
    },
    chatbot_list: {
      id: 'chatbot_list',
      icon: 'i-lucide-list-tree',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_LIST.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_LIST.DESCRIPTION'
      ),
      preview: previewCatalog.chatbot_list,
    },
    cards_carousel: {
      id: 'cards_carousel',
      icon: 'i-lucide-panels-top-left',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CARDS_CAROUSEL.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CARDS_CAROUSEL.DESCRIPTION'
      ),
      preview: previewCatalog.cards_carousel,
    },
  };

  return flowTemplateIds.map(templateId => templates[templateId]);
});

const availableWhatsAppInbox = computed(() =>
  inboxes.value.find(inbox => {
    return (
      inbox.channel_type === INBOX_TYPES.WHATSAPP ||
      (inbox.channel_type === INBOX_TYPES.TWILIO &&
        inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP)
    );
  })
);

const openFlow = flowId => {
  router.push({
    name: 'conversation_flow_editor',
    params: { flowId },
  });
};

const createFlow = () => {
  if (!availableWhatsAppInbox.value) {
    useAlert(t('CONVERSATION_FLOWS.CREATE.NO_INBOX_MESSAGE'));
    return;
  }

  router.push({
    name: 'conversation_flow_editor',
    params: { flowId: 'new' },
    query: { inboxId: availableWhatsAppInbox.value.id },
  });
};

const openImportPicker = () => {
  if (!availableWhatsAppInbox.value) {
    useAlert(t('CONVERSATION_FLOWS.CREATE.NO_INBOX_MESSAGE'));
    return;
  }

  importInputRef.value?.click();
};

const importFlowAsNew = async event => {
  const [file] = event.target.files || [];
  event.target.value = '';

  if (!file) return;

  try {
    const content = await file.text();
    const payload = JSON.parse(content);
    const importToken = `flow-import-${Date.now()}`;

    sessionStorage.setItem(
      importToken,
      JSON.stringify({
        name: payload.name,
        description: payload.description,
        definition: payload.definition || payload,
      })
    );

    router.push({
      name: 'conversation_flow_editor',
      params: { flowId: 'new' },
      query: {
        inboxId: availableWhatsAppInbox.value.id,
        importToken,
      },
    });
  } catch (error) {
    useAlert(t('CONVERSATION_FLOWS.EDITOR.IMPORT_ERROR'));
  }
};

const openTemplatePreview = template => {
  selectedTemplate.value = template;
  previewDialogRef.value.open();
};

const createFlowFromTemplate = async template => {
  if (!availableWhatsAppInbox.value) {
    useAlert(t('CONVERSATION_FLOWS.CREATE.NO_INBOX_MESSAGE'));
    return;
  }

  creatingTemplateId.value = template.id;

  try {
    router.push({
      name: 'conversation_flow_editor',
      params: { flowId: 'new' },
      query: {
        inboxId: availableWhatsAppInbox.value.id,
        templateId: template.id,
      },
    });
  } catch (error) {
    useAlert(t('CONVERSATION_FLOWS.TEMPLATES.CREATE_ERROR'));
  } finally {
    creatingTemplateId.value = '';
  }
};

const openDeletePopup = flow => {
  selectedFlow.value = flow;
  deleteDialogRef.value.open();
};

const confirmDeletion = async () => {
  loading.value[selectedFlow.value.id] = true;
  try {
    await store.dispatch('conversationFlows/delete', selectedFlow.value.id);
    useAlert(t('CONVERSATION_FLOWS.DELETE.SUCCESS_MESSAGE'));
    deleteDialogRef.value.close();
  } catch (error) {
    useAlert(t('CONVERSATION_FLOWS.DELETE.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedFlow.value.id] = false;
    selectedFlow.value = {};
  }
};

const exportFlow = flow => {
  const payload = {
    name: flow.name,
    channel_type: flow.channel_type,
    inbox_id: flow.inbox_id,
    definition: flow.definition,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${flow.name || 'flow-builder'}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
};

onMounted(() => {
  store.dispatch('conversationFlows/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CONVERSATION_FLOWS.LOADING')"
    :no-records-found="false"
    :no-records-message="$t('CONVERSATION_FLOWS.INDEX.EMPTY_STATE.DESCRIPTION')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('CONVERSATION_FLOWS.INDEX.HEADER.TITLE')"
        :description="$t('CONVERSATION_FLOWS.INDEX.HEADER.DESCRIPTION')"
        :search-placeholder="$t('CONVERSATION_FLOWS.SEARCH_PLACEHOLDER')"
        feature-name="conversation-flows"
        compact-spacing
      >
        <template v-if="flows?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.COUNT', { n: flows.length }) }}
          </span>
        </template>
        <template #actions>
          <input
            ref="importInputRef"
            type="file"
            accept="application/json"
            class="hidden"
            @change="importFlowAsNew"
          />
          <Button
            :label="$t('CONVERSATION_FLOWS.ACTIONS.IMPORT_JSON')"
            icon="i-lucide-upload"
            size="sm"
            slate
            @click="openImportPicker"
          />
          <Button
            :label="$t('CONVERSATION_FLOWS.CREATE.BUTTON')"
            icon="i-lucide-circle-plus"
            size="sm"
            @click="createFlow"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <section
        class="mt-2 border rounded-xl border-n-weak bg-n-alpha-1 p-5 shadow-sm"
      >
        <div
          class="flex flex-col gap-2 pb-4 border-b border-n-weak md:flex-row md:items-end md:justify-between"
        >
          <div class="flex flex-col gap-1">
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ $t('CONVERSATION_FLOWS.TEMPLATES.TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.TEMPLATES.DESCRIPTION') }}
            </p>
          </div>
          <span class="text-xs text-n-slate-10">
            {{ $t('CONVERSATION_FLOWS.TEMPLATES.HELP') }}
          </span>
        </div>

        <div class="grid grid-cols-1 gap-4 mt-5 xl:grid-cols-4 md:grid-cols-2">
          <article
            v-for="template in flowTemplates"
            :key="template.id"
            class="flex flex-col gap-4 border rounded-xl border-n-weak bg-n-solid-1 p-4 shadow-sm"
          >
            <div
              class="overflow-hidden border rounded-xl border-n-weak bg-n-alpha-1"
            >
              <img
                :src="template.preview.heroImage"
                :alt="template.title"
                class="object-contain object-top w-full h-80 bg-white"
              />
            </div>

            <div class="flex flex-col gap-1 min-h-[2.75rem]">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ template.title }}
              </h3>
              <p class="text-xs leading-5 text-n-slate-11">
                {{ template.description }}
              </p>
            </div>

            <div class="flex items-center gap-2 mt-auto">
              <Button
                slate
                size="sm"
                class="flex-1"
                :label="$t('CONVERSATION_FLOWS.TEMPLATES.PREVIEW')"
                @click="openTemplatePreview(template)"
              />
              <Button
                size="sm"
                class="flex-1"
                :is-loading="creatingTemplateId === template.id"
                :label="$t('CONVERSATION_FLOWS.TEMPLATES.USE_TEMPLATE')"
                @click="createFlowFromTemplate(template)"
              />
            </div>
          </article>
        </div>
      </section>
    </template>

    <template #body>
      <div
        class="mt-4 overflow-hidden border rounded-xl border-n-weak bg-n-solid-1 shadow-sm"
      >
        <BaseTable
          :headers="tableHeaders"
          :items="filteredFlows"
          fixed-layout
          :no-data-message="
            searchQuery
              ? $t('CONVERSATION_FLOWS.NO_RESULTS')
              : $t('CONVERSATION_FLOWS.INDEX.EMPTY_STATE.DESCRIPTION')
          "
        >
          <template #header-0>
            <span class="inline-block w-12 text-center rtl:pr-4 ltr:pl-4">
              {{ tableHeaders[0] }}
            </span>
          </template>
          <template #header-1>
            <span class="inline-block w-[12rem] md:w-[14rem]">
              {{ $t('CONVERSATION_FLOWS.TABLE_HEADER.FLOW_NAME') }}
            </span>
          </template>
          <template #header-2>
            <span class="inline-block w-24">
              {{ $t('CONVERSATION_FLOWS.TABLE_HEADER.STATUS') }}
            </span>
          </template>
          <template #header-3>
            <span class="inline-block w-14 text-center">
              {{ $t('CONVERSATION_FLOWS.TABLE_HEADER.INBOX') }}
            </span>
          </template>
          <template #header-4>
            <span class="inline-block w-44">
              {{ $t('CONVERSATION_FLOWS.TABLE_HEADER.UPDATED_AT') }}
            </span>
          </template>
          <template #header-5>
            <span class="inline-block w-28 text-end rtl:pl-4 ltr:pr-4">
              {{ $t('CONVERSATION_FLOWS.TABLE_HEADER.ACTIONS') }}
            </span>
          </template>
          <template #row="{ items }">
            <ConversationFlowRow
              v-for="(flow, index) in items"
              :key="flow.id"
              :flow="flow"
              :index="index + 1"
              :loading="loading[flow.id]"
              @open="openFlow"
              @export="exportFlow"
              @delete="openDeletePopup"
            />
          </template>
        </BaseTable>
      </div>
    </template>

    <Dialog
      ref="previewDialogRef"
      type="edit"
      :title="selectedTemplate.title"
      :description="selectedTemplate.description"
      :confirm-button-label="$t('CONVERSATION_FLOWS.TEMPLATES.USE_TEMPLATE')"
      :cancel-button-label="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_NO')"
      @confirm="createFlowFromTemplate(selectedTemplate)"
    >
      <div class="flex flex-col gap-4">
        <div
          class="overflow-hidden border rounded-2xl border-n-weak bg-n-alpha-1 p-3"
        >
          <div class="mx-auto max-w-[22rem]">
            <img
              :src="selectedTemplate.preview?.heroImage"
              :alt="selectedTemplate.title"
              class="object-contain object-top w-full rounded-2xl max-h-[38rem] bg-white"
            />
          </div>
        </div>

        <div
          class="grid grid-cols-1 gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <div class="rounded-lg bg-n-solid-1 px-3 py-2">
            <p class="text-xs text-n-slate-10">
              {{ $t('CONVERSATION_FLOWS.TEMPLATES.PREVIEW_LABELS.TYPE') }}
            </p>
            <p class="mt-1 text-sm font-medium text-n-slate-12">
              {{ selectedTemplate.title }}
            </p>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_TITLE')"
      :description="
        $t('CONVERSATION_FLOWS.DELETE.CONFIRM_MESSAGE', {
          name: selectedFlow.name,
        })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_YES')"
      :cancel-button-label="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
